import { useCallback, useEffect, useRef, useState } from "react";

import {
  MIN_REGION_PX,
  clampDockSize,
  dockSizeFromPointer,
  loadAxis,
  loadDockSize,
  saveAxis,
  saveDockSize,
  type SplitAxis,
} from "@/lib/split";

interface SplitPaneProps {
  /** Namespaces the persisted size and axis. Read and Cards each keep their own. */
  surface: string;
  main: React.ReactNode;
  dock: React.ReactNode;
  /** Per-surface default dock size: 560 for Read, 520 for the Cards note. */
  defaultDockSize?: number;
  /**
   * A floor the dock is held at while some content needs the room — "opening the picker just drags
   * the split up; closing it drops back to results". It does not touch the persisted ratio, and a
   * deliberate drag wins over it, so it raises the floor without taking the grip away.
   */
  minDockSize?: number;
  axis?: SplitAxis;
  onAxisChange?: (axis: SplitAxis) => void;
  /**
   * The scrolling main region, for a surface that has to know its own scroll position.
   *
   * Read is the only caller (日本-602): windowing the sentence list needs `scrollTop` and
   * `clientHeight` of the box that scrolls, and that box is **this component's**, not the
   * surface's. Handed down rather than found by walking up from the surface's own container,
   * which would silently start measuring the wrong element the first time this markup changed.
   */
  mainRef?: React.Ref<HTMLDivElement>;
}

/**
 * The split primitive behind Read and Cards: a main region, an 11px draggable grip, and a detail
 * dock.
 *
 * Both regions scroll independently — that is the point, and the most common way to get this wrong
 * is to let the page scroll instead. The ratio persists per surface; the axis persists per
 * window-width bucket.
 */
export function SplitPane({
  surface,
  main,
  dock,
  defaultDockSize = 560,
  minDockSize,
  axis: axisProp,
  onAxisChange,
  mainRef,
}: SplitPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [axis, setAxis] = useState<SplitAxis>(() =>
    axisProp ?? loadAxis(surface, window.innerWidth),
  );
  const [dockSize, setDockSize] = useState<number>(() =>
    loadDockSize(surface, axis, axis === "side-by-side" ? 820 : defaultDockSize),
  );
  const [dragging, setDragging] = useState(false);
  // Reset per opening: the floor applies until you overrule it, then stays overruled until the
  // content that asked for it goes away and comes back.
  const [floorOverruled, setFloorOverruled] = useState(false);
  useEffect(() => {
    setFloorOverruled(false);
  }, [minDockSize]);

  useEffect(() => {
    if (axisProp && axisProp !== axis) {
      setAxis(axisProp);
      setDockSize(loadDockSize(surface, axisProp, axisProp === "side-by-side" ? 820 : defaultDockSize));
    }
  }, [axisProp, axis, surface, defaultDockSize]);

  useEffect(() => {
    saveAxis(surface, window.innerWidth, axis);
    onAxisChange?.(axis);
  }, [axis, surface, onAxisChange]);

  const vertical = axis === "stacked";
  const effectiveDockSize =
    minDockSize && !floorOverruled ? Math.max(dockSize, minDockSize) : dockSize;

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      setFloorOverruled(true);
      setDragging(true);
      const rect = container.getBoundingClientRect();

      const move = (e: PointerEvent) => {
        const next = vertical
          ? dockSizeFromPointer(e.clientY, rect.top, rect.height)
          : dockSizeFromPointer(e.clientX, rect.left, rect.width);
        setDockSize(next);
      };
      const up = () => {
        setDragging(false);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        setDockSize((current) => {
          saveDockSize(surface, axis, current);
          return current;
        });
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [vertical, surface, axis],
  );

  // Keyboard resize, because the grip is a control and every control is reachable by keyboard.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const step = event.shiftKey ? 48 : 12;
      const grow = vertical ? "ArrowUp" : "ArrowLeft";
      const shrink = vertical ? "ArrowDown" : "ArrowRight";
      if (event.key !== grow && event.key !== shrink) return;
      event.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      setFloorOverruled(true);
      const total = vertical ? container.clientHeight : container.clientWidth;
      setDockSize((current) => {
        const next = clampDockSize(current + (event.key === grow ? step : -step), total);
        saveDockSize(surface, axis, next);
        return next;
      });
    },
    [vertical, surface, axis],
  );

  return (
    <div
      ref={containerRef}
      className={["flex min-h-0 min-w-0 flex-1", vertical ? "flex-col" : "flex-row"].join(" ")}
    >
      <div ref={mainRef} className="min-h-0 min-w-0 flex-1 overflow-auto">
        {main}
      </div>

      <div
        role="separator"
        aria-orientation={vertical ? "horizontal" : "vertical"}
        aria-label="詳細パネルの幅を変更"
        aria-valuenow={Math.round(effectiveDockSize)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        className={[
          "flex shrink-0 items-center justify-center gap-[3px] bg-[var(--resizer-bg)]",
          vertical
            ? "h-[11px] cursor-ns-resize border-y border-t-[var(--resizer-border)] border-b-line-strong"
            : "w-[11px] flex-col cursor-ew-resize border-x border-l-[var(--resizer-border)] border-r-line-strong",
          dragging ? "bg-line-strong" : "",
        ].join(" ")}
      >
        {[0, 1].map((i) => (
          <span
            key={i}
            className={[
              "rounded-[var(--r-2)] bg-[var(--resizer-grip)]",
              vertical ? "h-[2px] w-[26px]" : "h-[26px] w-[2px]",
            ].join(" ")}
          />
        ))}
      </div>

      <div
        className="min-h-0 min-w-0 shrink-0 overflow-auto bg-sunken"
        // The cap is CSS rather than clampDockSize because the floor can push past the container:
        // percentages resolve against the real height, so the main region keeps its minimum without
        // this component having to measure anything.
        style={
          vertical
            ? { height: effectiveDockSize, maxHeight: `calc(100% - ${MIN_REGION_PX}px)` }
            : { width: effectiveDockSize, maxWidth: `calc(100% - ${MIN_REGION_PX}px)` }
        }
      >
        {dock}
      </div>
    </div>
  );
}
